import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { DEFAULT_TIMER_SECONDS, EXTEND_SECONDS } from "./data/attendanceConstants";
import type { AttendanceGroup, RoundStats, StartRoundResponse, TrainerLiveState } from "./types/attendance";
import {
  extendAttendanceRound,
  fetchAttendanceConfig,
  fetchLatestSessionRound,
  fetchPlannedGroups,
  fetchRoundGroups,
  fetchRoundStats,
  mapGroup,
  mapRoundStats,
  startAttendanceRound,
  updateAttendanceHeadcount,
} from "./services/attendanceApi";
import { resolveAttendanceImageUrl } from "./utils/attendanceImageUrl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import {
  isAuthHttpError,
  isAuthSessionExpired,
} from "../utils/authSessionGuard";
import { downloadGroupsAsImage } from "./utils/downloadGroupsAsImage";
import { AttendancePageShell } from "./utils/AttendancePageShell";

const GROUP_IMAGE_STYLE = { width: 140, height: 140, objectFit: "contain" as const };
const STATS_POLL_MS = Number(process.env.REACT_APP_ATTENDANCE_POLL_MS) || 3000;

function GroupImageGrid({
  groups,
  title,
}: {
  groups: AttendanceGroup[];
  title: string;
}) {
  if (groups.length === 0) return null;

  return (
    <>
      <h4 className="fw-bold mb-3">{title}</h4>
      <Row className="g-4">
        {groups.map((group) => (
          <Col md={4} key={group.id}>
            <Card className="h-100 border-start border-primary border-4">
              <Card.Body className="text-center">
                <h5 className="fw-bold mb-1">{group.name}</h5>
                <p className="text-muted mb-3">
                  {group.studentCount} student{group.studentCount !== 1 ? "s" : ""}
                </p>
                {group.correctImageUrl ? (
                  <img
                    src={resolveAttendanceImageUrl(group.correctImageUrl)}
                    alt={group.name}
                    className="img-fluid border border-primary border-3 rounded object-fit-contain"
                    style={GROUP_IMAGE_STYLE}
                  />
                ) : (
                  <p className="text-muted">Image not available</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </>
  );
}

function GroupImagesPreviewModal({
  show,
  groups,
  downloading,
  onHide,
  onDownload,
}: {
  show: boolean;
  groups: AttendanceGroup[];
  downloading: boolean;
  onHide: () => void;
  onDownload: () => void;
}) {
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Images preview</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <Row className="g-4 justify-content-center">
          {groups.map((group) => (
            <Col xs={12} sm={6} md={4} key={group.id} className="text-center">
              {group.correctImageUrl && (
                <img
                  src={resolveAttendanceImageUrl(group.correctImageUrl)}
                  alt={group.name}
                  className="img-fluid border border-primary border-3 rounded object-fit-contain"
                  style={{ ...GROUP_IMAGE_STYLE, width: 200, height: 200 }}
                />
              )}
            </Col>
          ))}
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={onDownload}
          disabled={downloading || groups.length === 0}
        >
          {downloading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Downloading...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              Download
            </>
          )}
        </Button>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function AttendanceSplitBar({
  presentPercent,
  absentPercent,
  presentCount,
  absentCount,
}: {
  presentPercent: number;
  absentPercent: number;
  presentCount: number;
  absentCount: number;
}) {
  return (
    <div className="mb-4 mt-2">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="fw-semibold text-success">
          Present {presentCount} · {presentPercent}%
        </span>
        <span className="fw-semibold text-danger">
          Absent {absentCount} · {absentPercent}%
        </span>
      </div>
      <div
        className="rounded-3 overflow-hidden d-flex w-100"
        style={{ height: "28px", backgroundColor: "#e9ecef" }}
        role="progressbar"
        aria-valuenow={presentPercent + absentPercent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          style={{
            width: `${presentPercent}%`,
            backgroundColor: "#198754",
            height: "100%",
            transition: "width 0.3s ease",
          }}
        />
        <div
          style={{
            width: `${absentPercent}%`,
            backgroundColor: "#dc3545",
            height: "100%",
            transition: "width 0.3s ease",
          }}
        />
      </div>
    </div>
  );
}

const AttendanceLive: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const liveState = location.state as TrainerLiveState | null;

  const [extendSeconds, setExtendSeconds] = useState(EXTEND_SECONDS);
  const [defaultTimerSeconds, setDefaultTimerSeconds] = useState(DEFAULT_TIMER_SECONDS);
  const [sessionTotalStudents, setSessionTotalStudents] = useState(
    liveState?.totalStudents ?? 0,
  );
  const [timerSeconds, setTimerSeconds] = useState(DEFAULT_TIMER_SECONDS);
  const [isActive, setIsActive] = useState(false);
  const [isLaunched, setIsLaunched] = useState(false);
  const [headCount, setHeadCount] = useState<number | "">("");
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewModalGroups, setPreviewModalGroups] = useState<AttendanceGroup[]>([]);
  const [downloadingImages, setDownloadingImages] = useState(false);
  const [groups, setGroups] = useState<AttendanceGroup[]>([]);
  const [plannedGroups, setPlannedGroups] = useState<AttendanceGroup[]>([]);
  const [roundStats, setRoundStats] = useState<RoundStats | null>(null);
  const [attendanceRoundId, setAttendanceRoundId] = useState<string | null>(
    liveState?.attendanceRoundId ?? null,
  );
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const headCountDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headCountDirtyRef = useRef(false);
  const headCountSaveGenRef = useRef(0);
  const statsPollInFlightRef = useRef(false);
  const timerSecondsRef = useRef(timerSeconds);

  const currentRound = roundStats?.roundNo ?? 1;

  const applyServerHeadCount = (value: number | null | undefined) => {
    if (headCountDirtyRef.current) return;
    if (value != null) setHeadCount(value);
  };

  useEffect(() => {
    timerSecondsRef.current = timerSeconds;
  }, [timerSeconds]);

  const applyRoundResponse = useCallback((response: StartRoundResponse) => {
    setAttendanceRoundId(response.attendance_round_id);
    setGroups(response.groups.map(mapGroup));
    const stats = mapRoundStats(response.round_stats);
    setRoundStats(stats);
    setSessionTotalStudents(stats.total);
    timerSecondsRef.current = stats.timerSeconds;
    setTimerSeconds(stats.timerSeconds);
    setIsActive(stats.timerSeconds > 0);
    setIsLaunched(true);
    if (stats.manualHeadCount != null) {
      setHeadCount(stats.manualHeadCount);
    } else {
      setHeadCount("");
    }
  }, []);

  const persistLiveRoundId = useCallback(
    (roundId: string) => {
      if (!liveState || liveState.attendanceRoundId === roundId) return;
      navigate(".", {
        replace: true,
        state: {
          ...liveState,
          attendanceRoundId: roundId,
          hasPriorRounds: true,
        },
      });
    },
    [liveState, navigate],
  );

  useEffect(() => {
    if (!liveState?.attendanceSessionId) return;

    const initPage = async () => {
      setLoading(true);
      setError("");
      try {
        const config = await fetchAttendanceConfig();
        setExtendSeconds(config.extendSeconds);
        setDefaultTimerSeconds(config.defaultTimerSeconds);

        const resumeRound = async (roundId: string) => {
          const [stats, roundGroups] = await Promise.all([
            fetchRoundStats(roundId),
            fetchRoundGroups(roundId),
          ]);
          setRoundStats(stats);
          setGroups(roundGroups);
          setSessionTotalStudents(stats.total);
          timerSecondsRef.current = stats.timerSeconds;
          setTimerSeconds(stats.timerSeconds);
          setAttendanceRoundId(roundId);
          setIsActive(stats.timerSeconds > 0);
          setIsLaunched(true);
          if (stats.manualHeadCount != null) {
            setHeadCount(stats.manualHeadCount);
          }
          persistLiveRoundId(roundId);
        };

        if (liveState.attendanceRoundId) {
          await resumeRound(liveState.attendanceRoundId);
        } else {
          const latest = await fetchLatestSessionRound(liveState.attendanceSessionId);
          if (latest) {
            applyRoundResponse(latest);
            persistLiveRoundId(latest.attendance_round_id);
          } else {
            setTimerSeconds(config.defaultTimerSeconds);
            setIsLaunched(false);
            setIsActive(false);
            const planned = await fetchPlannedGroups(liveState.attendanceSessionId);
            setPlannedGroups(planned);
          }
        }
      } catch (err) {
        console.error("Error loading attendance live page:", err);
        setError("Failed to load attendance session. Please go back and try again.");
      } finally {
        setLoading(false);
      }
    };

    void initPage();
  }, [liveState, applyRoundResponse, persistLiveRoundId]);

  useEffect(() => {
    if (timerSeconds <= 0) setIsActive(false);
  }, [timerSeconds]);

  useEffect(() => {
    if (!isLaunched || !isActive || loading) return;

    const interval = setInterval(() => {
      const prev = timerSecondsRef.current;
      if (prev <= 0) return;
      const next = Math.max(0, prev - 1);
      timerSecondsRef.current = next;
      setTimerSeconds(next);
      if (next === 0) setIsActive(false);
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive, isLaunched, loading]);

  useEffect(() => {
    if (!attendanceRoundId || loading || !isLaunched) return;

    let interval: ReturnType<typeof setInterval> | undefined;
    const poll = async () => {
      if (statsPollInFlightRef.current || isAuthSessionExpired()) return;
      statsPollInFlightRef.current = true;
      try {
        const stats = await fetchRoundStats(attendanceRoundId);
        setRoundStats(stats);
        setSessionTotalStudents(stats.total);
        const local = timerSecondsRef.current;
        const server = stats.timerSeconds;
        // Only pull server timer up (extend) or force to 0 when expired.
        // Do not reset local countdown on every poll — that caused 13→12→11→13 loops.
        if (server > local) {
          timerSecondsRef.current = server;
          setTimerSeconds(server);
          setIsActive(true);
        } else if (server <= 0) {
          timerSecondsRef.current = 0;
          setTimerSeconds(0);
          setIsActive(false);
        }
        if (stats.manualHeadCount != null) {
          applyServerHeadCount(stats.manualHeadCount);
        }
      } catch (err) {
        if (isAuthHttpError(err) || isAuthSessionExpired()) {
          if (interval != null) clearInterval(interval);
        }
        /* silent poll failure */
      } finally {
        statsPollInFlightRef.current = false;
      }
    };

    void poll();
    interval = setInterval(() => void poll(), STATS_POLL_MS);
    return () => {
      if (interval != null) clearInterval(interval);
    };
  }, [attendanceRoundId, loading, isLaunched]);

  const handleLaunch = async () => {
    if (!liveState?.attendanceSessionId) return;
    setActionLoading(true);
    setError("");
    try {
      const response = await startAttendanceRound(
        liveState.attendanceSessionId,
        defaultTimerSeconds,
      );
      applyRoundResponse(response);
      persistLiveRoundId(response.attendance_round_id);
    } catch (err) {
      console.error("Error launching attendance round:", err);
      setError("Failed to launch attendance. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtend = async () => {
    if (!attendanceRoundId) return;
    setActionLoading(true);
    setError("");
    try {
      const stats = await extendAttendanceRound(attendanceRoundId, extendSeconds);
      setRoundStats(stats);
      setSessionTotalStudents(stats.total);
      timerSecondsRef.current = stats.timerSeconds;
      setTimerSeconds(stats.timerSeconds);
      setIsActive(stats.timerSeconds > 0);
      if (stats.manualHeadCount != null) {
        applyServerHeadCount(stats.manualHeadCount);
      }
    } catch (err) {
      console.error("Error extending round:", err);
      setError("Failed to extend timer.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleHeadCountChange = (value: number | "") => {
    setHeadCount(value);
    headCountDirtyRef.current = true;
    if (headCountDebounceRef.current) clearTimeout(headCountDebounceRef.current);

    if (!attendanceRoundId || value === "") {
      return;
    }

    const saveGen = ++headCountSaveGenRef.current;
    headCountDebounceRef.current = setTimeout(() => {
      void updateAttendanceHeadcount(attendanceRoundId, value)
        .then((stats) => {
          if (saveGen !== headCountSaveGenRef.current) return;
          setRoundStats(stats);
          headCountDirtyRef.current = false;
          if (stats.manualHeadCount != null) {
            setHeadCount(stats.manualHeadCount);
          }
        })
        .catch((err) => console.error("Error updating head count:", err));
    }, 600);
  };

  useEffect(
    () => () => {
      if (headCountDebounceRef.current) clearTimeout(headCountDebounceRef.current);
    },
    [],
  );

  const openPreviewImages = useCallback((groupList: AttendanceGroup[]) => {
    setPreviewModalGroups(groupList);
    setShowPreviewModal(true);
  }, []);

  const handleDownloadImages = useCallback(async () => {
    if (previewModalGroups.length === 0) return;
    setDownloadingImages(true);
    setError("");
    try {
      await downloadGroupsAsImage(previewModalGroups);
    } catch (err) {
      console.error("Failed to download group images:", err);
      setError("Failed to download images. Please try again.");
    } finally {
      setDownloadingImages(false);
    }
  }, [previewModalGroups]);

  if (!liveState) {
    return (
      <AttendancePageShell>
        <Alert variant="warning">
          No attendance session found. Please start from{" "}
          <Button variant="link" className="p-0 align-baseline" onClick={() => navigate("/admin/attendance/setup")}>
            Attendance Setup
          </Button>
          .
        </Alert>
      </AttendancePageShell>
    );
  }

  const stats = roundStats ?? {
    presentPercent: 0,
    absentPercent: 0,
    roundNo: currentRound,
    roundLabel: `Att 0${currentRound}`,
    total: liveState.totalStudents,
    timerSeconds: 0,
    presentCount: 0,
    absentCount: 0,
  };

  return (
    <>
      <AttendancePageShell style={{ position: "relative" }}>
        <div
          style={{
            filter: loading ? "blur(2px)" : undefined,
            pointerEvents: loading ? "none" : undefined,
            userSelect: loading ? "none" : undefined,
            transition: "filter 0.15s ease",
          }}
          aria-busy={loading}
        >
          {error && (
            <Alert variant="danger" className="mb-3" onClose={() => setError("")} dismissible>
              {error}
            </Alert>
          )}

          {liveState.returnToTestPath && (
            <div className="d-flex justify-content-end mb-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => navigate(liveState.returnToTestPath!)}
              >
                Return to Test
              </Button>
            </div>
          )}

          <Card className="border-0 shadow-sm rounded-3 mb-2">
            <Card.Body>
              <Row className="g-2">
                <Col md={3}>
                  <span className="text-muted small d-block">Course</span>
                  <strong>{liveState.course}</strong>
                </Col>
                <Col md={3}>
                  <span className="text-muted small d-block">Batch</span>
                  <strong>{liveState.batches.join(", ")}</strong>
                </Col>
                <Col md={3}>
                  <span className="text-muted small d-block">
                    {liveState.setupMode === "test"
                      ? "Event"
                      : liveState.setupMode === "onsite"
                        ? "Subject"
                        : "Event"}
                  </span>
                  <strong>{liveState.purposeDetail}</strong>
                </Col>
                <Col md={3}>
                  <span className="text-muted small d-block">Date</span>
                  <strong>{liveState.dateTime}</strong>
                </Col>
                {liveState.setupMode === "test" && liveState.eventType && (
                  <Col md={3} className="mt-2">
                    <span className="text-muted small d-block">Event type</span>
                    <strong>{liveState.eventType}</strong>
                  </Col>
                )}
                <Col md={3} className="mt-2">
                  <span className="text-muted small d-block">Total Students</span>
                  <strong>
                    {roundStats?.total ?? sessionTotalStudents ?? liveState.totalStudents}
                  </strong>
                </Col>
                <Col md={3} className="mt-2">
                  <span className="text-muted small d-block">Groups</span>
                  <strong>
                    {isLaunched
                      ? groups.length
                      : plannedGroups.length > 0
                        ? plannedGroups.length
                        : liveState.groupCount ||
                          liveState.groupSizes?.length ||
                          "—"}
                  </strong>
                </Col>
                <Col md={3} className="mt-2">
                  <span className="text-muted small d-block">Session ID</span>
                  <strong>{liveState.attendanceSessionId}</strong>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {!isLaunched ? (
            <Card className="border-0 shadow-sm rounded-3 mb-2">
              <Card.Body className="p-3">
                {plannedGroups.length > 0 ? (
                  <>
                    <div className="d-flex justify-content-end">
                      <Button
                        variant="outline-primary"
                        onClick={() => openPreviewImages(plannedGroups)}
                        disabled={loading}
                      >
                        Preview Images
                      </Button>
                    </div>
                    <GroupImageGrid
                      groups={plannedGroups}
                      title={`Groups (${plannedGroups.length})`}
                    />
                  </>
                ) : (liveState.groupSizes?.length ?? 0) > 0 ? (
                  <div className="bg-light rounded-3 p-4 mb-4">
                    <h5 className="fw-semibold mb-2">
                      {liveState.groupCount} group{liveState.groupCount !== 1 ? "s" : ""} planned
                    </h5>
                    <div className="d-flex flex-wrap gap-2">
                      {liveState.groupSizes.map((size, index) => (
                        <Badge key={index} bg="secondary" className="px-3 py-2">
                          Group {index + 1}: {size} students
                        </Badge>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="text-center mt-2">
                  <Button
                    variant="primary"
                    size="lg"
                    className="px-5"
                    onClick={() => void handleLaunch()}
                    disabled={actionLoading || loading}
                  >
                    {actionLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Launching...
                      </>
                    ) : (
                      "Launch Attendance"
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <Card className="border-0 shadow-sm rounded-3">
              <Card.Body className="p-4">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                  <h2 className="text-primary fw-bold mb-0">
                    {stats.roundLabel || `Attendance No ${currentRound}`}
                  </h2>
                  <Badge bg={timerSeconds > 0 ? "primary" : "danger"} className="fs-5 px-3 py-2">
                    {timerSeconds > 0 ? `${timerSeconds}s remaining` : "Time Up"}
                  </Badge>
                </div>

                <AttendanceSplitBar
                  presentPercent={stats.presentPercent}
                  absentPercent={stats.absentPercent}
                  presentCount={stats.presentCount}
                  absentCount={stats.absentCount}
                />

                <Row className="g-3 mb-3 align-items-end">
                  <Col md={4}>
                    <Form.Label className="text-muted small fw-semibold">Manual Head Count</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      placeholder="Enter number of students present"
                      value={headCount}
                      disabled={loading}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          handleHeadCountChange("");
                          return;
                        }
                        const num = Number(value);
                        if (!Number.isNaN(num) && num >= 0) handleHeadCountChange(num);
                      }}
                    />
                  </Col>
                  <Col md={4}>
                    <div className="bg-light border-start border-success border-4 rounded p-3">
                      <span className="text-muted small d-block">Marked Present</span>
                      <strong className="fs-4">{stats.presentCount}</strong>
                      <span className="text-muted ms-2">
                        student{stats.presentCount !== 1 ? "s" : ""} marked present
                      </span>
                    </div>
                  </Col>
                  {headCount !== "" && (
                    <Col md={4}>
                      <div className="bg-light border-start border-primary border-4 rounded p-3">
                        <span className="text-muted small d-block">Recorded Head Count</span>
                        <strong className="fs-4">{headCount}</strong>
                        <span className="text-muted ms-2">students present</span>
                      </div>
                    </Col>
                  )}
                </Row>
                {headCount !== "" && Number(headCount) !== stats.presentCount && (
                  <Alert variant="warning" className="mb-4">
                    Head count ({headCount}) does not match students marked present ({stats.presentCount}).
                  </Alert>
                )}

                <div className="d-flex flex-wrap gap-2 mb-4">
                  <Button
                    variant="outline-primary"
                    onClick={() => void handleExtend()}
                    disabled={actionLoading || loading}
                  >
                    Extend (+{extendSeconds}s)
                  </Button>
                  {groups.length > 0 && (
                    <Button
                      variant="outline-primary"
                      onClick={() => openPreviewImages(groups)}
                      disabled={loading}
                    >
                      Preview Images
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    onClick={() =>
                      navigate("/admin/attendance/report", {
                        state: {
                          ...liveState,
                          attendanceRoundId:
                            attendanceRoundId ?? liveState.attendanceRoundId,
                        },
                      })
                    }
                    disabled={loading}
                  >
                    Report
                  </Button>
                </div>

                {groups.length > 0 && (
                  <GroupImageGrid groups={groups} title={`Groups (${groups.length})`} />
                )}
              </Card.Body>
            </Card>
          )}
        </div>

        {loading && (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 20,
              backgroundColor: "rgba(255, 255, 255, 0.35)",
              backdropFilter: "blur(1px)",
            }}
            role="status"
            aria-live="polite"
            aria-label="Loading"
          >
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-3 mb-0 small">Loading...</p>
          </div>
        )}
      </AttendancePageShell>

      <GroupImagesPreviewModal
        show={showPreviewModal}
        groups={previewModalGroups}
        downloading={downloadingImages}
        onHide={() => setShowPreviewModal(false)}
        onDownload={() => void handleDownloadImages()}
      />
    </>
  );
};

export default AttendanceLive;
